<template>
  <section class="onboarding-section">
    <div class="cards-container">
      <onboarding-card
        v-for="option in cardOptions"
        :key="option.key"
        :icon="{
          name: option.iconName,
          viewBox: option.iconViewBox,
          width: option.iconWidth || 20,
          height: option.iconHeight || 20
        }"
        :title="option.title"
        :description="$i18nPresenter.t(option.description)"
        :bg-color="option.bgColor"
        :action-name="option.actionName"
        :aha-other-info="option.key"
        :data-presentation-id="option.data.presentationId || ''"
        :is-loading="isLoading && selectedOptionKey === generateOptionKey(option.data)"
        :is-disabled="selectedOptionKey && selectedOptionKey !== generateOptionKey(option.data)"
        @click="handleOptionClick(option)"
      />
    </div>
  </section>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import CreateNewPresentationMixin from '@/mixins/presentation/CreateNewPresentationMixin.js'
import OnboardingCard from './OnboardingCard.vue'

const CURRENT_LOCALES = ['en', 'es', 'fr', 'zh', 'ru', 'vi']

export default {
  name: 'OnboardingSection',
  components: {
    OnboardingCard,
  },
  mixins: [CreateNewPresentationMixin],
  data() {
    return {
      selectedOptionKey: null,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      presenterLocale: (state) => state.presenterLocale,
      featureBasedOptions: (state) => state.onBoarding.featureBasedOptions,
    }),
    ...mapGetters('onBoarding', ['isLoading']),
    lang() {
      return CURRENT_LOCALES.includes(this.presenterLocale) ? this.presenterLocale : 'en'
    },
    pollOption() {
      return this.featureBasedOptions?.find((option) => option.key === 'poll') || {
        key: 'poll',
      }
    },
    quizOption() {
      return this.featureBasedOptions?.find((option) => option.key === 'quiz') || {
        key: 'quiz',
      }
    },
    wordCloudOption() {
      return this.featureBasedOptions?.find((option) => option.key === 'wordCloud') || {
        key: 'wordCloud',
      }
    },
    blankOption() {
      return { key: 'blank' }
    },
    cardOptions() {
      return [
        {
          key: 'blank',
          iconName: 'add-colored',
          iconViewBox: '0 0 24 24',
          iconWidth: 18,
          iconHeight: 18,
          title: 'Blank',
          description: 'Create your way.',
          bgColor: '#3E3E5A',
          actionName: 'homePageOnboardingSelectBlank',
          data: this.blankOption,
        },
        {
          key: 'quiz',
          iconName: 'trophy-colored',
          iconViewBox: '0 0 34 34',
          iconWidth: 22,
          iconHeight: 22,
          title: 'Quiz',
          description: 'Make learning a game.',
          bgColor: '#E6005C',
          actionName: 'homePageOnboardingSelectQuiz',
          data: this.quizOption,
        },
        {
          key: 'poll',
          iconName: 'poll-colored',
          iconViewBox: '0 0 24 24',
          iconWidth: 18,
          iconHeight: 18,
          title: 'Poll',
          description: 'Get instant opinions.',
          bgColor: '#5A189A',
          actionName: 'homePageOnboardingSelectPoll',
          data: this.pollOption,
        },
        {
          key: 'wordCloud',
          iconName: 'cloud-colored',
          iconViewBox: '0 0 32 20',
          iconWidth: 25,
          iconHeight: 25,
          title: 'Word Cloud',
          description: 'Highlight shared ideas.',
          bgColor: '#20E8B5',
          actionName: 'homePageOnboardingSelectWordCloud',
          data: this.wordCloudOption,
        },
        {
          key: 'templates',
          iconName: 'magic-wand-colored',
          iconViewBox: '0 0 26 26',
          iconWidth: 20,
          iconHeight: 20,
          title: 'Templates',
          description: 'Start with smart slides.',
          bgColor: '#FF9068',
          actionName: 'homePageOnboardingSelectTemplates',
          data: { key: 'templates' },
        },
      ]
    },
  },
  watch: {
    lang: {
      handler(value) {
        if (!value) return
        this.fetchFeatureBasedOptionsByLang(value)
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('onBoarding', ['getFeatureBasedOptionsAction', 'copyFromTemplateAction']),
    generateOptionKey(option) {
      return `${option.key}-${option.presentationId || 'blank'}`
    },
    async handleOptionClick(option) {
      if (option.key === 'templates') {
        this.handleTemplatesClick()
        return
      }

      const optionData = option.data
      this.selectedOptionKey = this.generateOptionKey(optionData)

      if (option.key === 'blank' || !optionData.presentationId) {
        this.$store.commit('onBoarding/setLoading', { type: 'copyTemplate', loading: true })
        this.createNewPresentation(`From home page ${option.key} card`)
        return
      }

      try {
        await this.copyFromTemplateAction({
          presentationId: optionData.presentationId,
          callback: this.handleCopyTemplateSuccess,
        })
      } catch (error) {
        Sentry.captureException(error)
        this.selectedOptionKey = null
      }
    },
    handleTemplatesClick() {
      this.$router.push({ name: 'TemplateLibrary' })
    },
    handleCopyTemplateSuccess(presentation) {
      const newPresentationId = presentation.id
      const params = { id: newPresentationId }
      this.$router.push({ name: 'PresentationDetail', params })
      this.selectedOptionKey = null
    },
    onCreateNewPresentationSuccess() {
      CreateNewPresentationMixin.methods.onCreateNewPresentationSuccess.call(this)
      this.selectedOptionKey = null
      this.$store.commit('onBoarding/setLoading', { type: 'copyTemplate', loading: false })
    },
    async fetchFeatureBasedOptionsByLang(lang) {
      await this.getFeatureBasedOptionsAction({
        language: lang,
        useAhaSlidesFor: this.currentUser.personalInformation && this.currentUser.personalInformation.useAhaSlidesFor,
        userProfession: this.currentUser.personalInformation && this.currentUser.personalInformation.userProfession,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.onboarding-section {
  margin-top: 4px;
  width: 100%;
  color: #282E3E;
  background-color: #fff;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0px 1px 3px 0px #D3D7E1CF;

  .cards-container {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 20px 12px;
  }
}

@media (min-width: 375px) {
  .onboarding-section {
    .cards-container {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

@media (min-width: 768px) {
  .onboarding-section {
    .cards-container {
      grid-template-columns: repeat(5, minmax(0, auto));
      gap: 12px;
    }
  }
}

@media (min-width: 1422px) { // scrollbar width ~ 17px
  .onboarding-section {
    padding: 8px;

    .cards-container {
      grid-template-columns: repeat(5, 1fr);
      gap: 16px;
    }
  }
}
</style>
